// =================================================================================
// File: src/layouts/VisitorShell.jsx
// Description: Visitor/Explorer shell with lightweight provider stack
// NO Vault, Storage, or workspace logic - public-facing experience only
// =================================================================================

import React from "react";
import { Routes, Route } from "react-router-dom";

// Light providers only (shared UI, no studio workspace logic)
import "../lib/visitorSupabaseGuard";
import { FeedbackProvider } from "../contexts/FeedbackContext";
import { NotificationsProvider } from "../contexts/NotificationsContext";
import { StudioProvider } from "../contexts/StudioContext"; // Required by PublicStudioProvider
import { PublicStudioProvider } from "../contexts/PublicStudioContext";
import { WorkshopProvider } from "../contexts/WorkshopContext";

// Common Components
import ToastHub from "../components/Common/ToastHub";
import Footer from "../components/Common/Footer";
import ErrorBoundary from "../components/Common/ErrorBoundary";

// Visitor Pages
import DiscoverHomePage from "../pages/visitor/DiscoverHomePage";
import DiscoverMapPage from "../pages/visitor/DiscoverMapPage";
import DiscoverWorkshopsPage from "../pages/visitor/DiscoverWorkshopsPage";
import BookStudioSpacePage from "../pages/visitor/BookStudioSpacePage";
import DiscoverMarketplacePage from "../pages/visitor/DiscoverMarketplacePage";
import PublicStudioPage from "../pages/visitor/PublicStudioPage";
import VisitorStudioDetail from "../pages/visitor/VisitorStudioDetail";
import VisitorProfilePage from "../pages/visitor/VisitorProfilePage";
import MyPiecesPage from "../pages/visitor/MyPiecesPage";
import MyOffersPage from "../pages/visitor/MyOffersPage.jsx";
import MyBookingsPage from "../pages/visitor/MyBookingsPage.jsx";
import NotFound from "../pages/NotFound";

/**
 * VisitorShell - Public discovery and exploration experience
 * 
 * Features:
 * - Browse studios on world map
 * - View studio public profiles
 * - Discover workshops and book classes
 * - Browse marketplace products
 * - NO workspace/storage/vault logic
 * - NO "Connect Google Drive" prompts
 * 
 * Provider Stack:
 * - FeedbackProvider (for UI feedback/toasts)
 * - NotificationsProvider (for app notifications)
 * - PublicStudioProvider (fetch public studio data)
 * 
 * Routes:
 * - /discover - Discovery home page
 * - /discover/map - Interactive studios map
 * - /discover/workshops - All public workshops
 * - /discover/bookings - Studio space booking
 * - /discover/marketplace - Product marketplace
 * - /discover/studio/:studioSlug - Public studio profile
 */
export default function VisitorShell() {
  return (
    <StudioProvider>
      <PublicStudioProvider>
        <WorkshopProvider>
          <FeedbackProvider>
            <NotificationsProvider>
              <div className="app-shell" style={{ minHeight: "100dvh", background: "#0f0f10", color: "#fff" }}>
                <div className="app-main">
                  <ErrorBoundary>
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading…</div>}>
                      <Routes>
                    {/* Discover Home */}
                    <Route path="/" element={<DiscoverHomePage />} />
                    
                    {/* Studios Map */}
                    <Route path="map" element={<DiscoverMapPage />} />
                    
                    {/* Workshops List */}
                    <Route path="workshops" element={<DiscoverWorkshopsPage />} />
                    
                    {/* Studio Space Booking */}
                    <Route path="bookings" element={<BookStudioSpacePage />} />
                    <Route path="bookings/:spaceId" element={<BookStudioSpacePage />} />
                    <Route path="book-studio" element={<BookStudioSpacePage />} />
                    
                    {/* Marketplace */}
                    <Route path="marketplace" element={<DiscoverMarketplacePage />} />

                    {/* Visitor Profile */}
                    <Route path="profile" element={<VisitorProfilePage />} />

                    {/* Visitor Account */}
                    <Route path="my-offers" element={<MyOffersPage />} />
                    <Route path="my-bookings" element={<MyBookingsPage />} />

                    {/* Pieces Tracking */}
                    <Route path="my-pieces" element={<MyPiecesPage />} />
                    
                    {/* Studio Detail - Shows workshops and spaces for a specific studio */}
                    <Route path="studios/:studioId" element={<VisitorStudioDetail />} />
                    
                    {/* Individual Studio Profile (legacy route) */}
                    <Route path="studio/:studioSlug" element={<PublicStudioPage />} />
                    
                    {/* 404 - Show not found page instead of redirecting */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                    </React.Suspense>
                  </ErrorBoundary>
                </div>

              <Footer />
              <ToastHub />
            </div>
          </NotificationsProvider>
        </FeedbackProvider>
        </WorkshopProvider>
      </PublicStudioProvider>
    </StudioProvider>
  );
}
